import { Box, Typography } from '@mui/material'
import { IconUploadFile } from 'assets/icons'
import { Button } from 'elements/inputs/button'
import { Accept, FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

type Props = {
  onDrop: (files: Array<File>) => void
  /** Maximum accepted number of files */
  maxFiles?: number
  /** Object with a common MIME type as keys and an array of file extensions as values */
  accept?: Accept
  /** Maximum file size (in bytes) */
  maxSize?: number
  /** Allow drag 'n' drop (or selection from the file dialog) of multiple files */
  multiple?: boolean
  disabled?: boolean
  children?: React.ReactNode
  onReject?: (fileRejections: FileRejection[]) => void
  isError?: boolean
}

export const DropZone = ({
  onDrop,
  maxFiles,
  accept,
  maxSize,
  multiple,
  disabled,
  children,
  onReject,
  isError,
}: Props) => {
  const { t } = useTranslation()

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    onDropAccepted: onDrop,
    maxSize,
    onDropRejected: onReject,
    accept,
    maxFiles,
    multiple,
    disabled,
  })

  const getDropZoneState = () => {
    if (isError)
      return {
        title: t('components.dropzone.failed_to_upload_file'),
        backgroundColor: 'error.light',
        textColor: 'error.main',
        iconColor: 'error.main',
      }
    if (isDragActive)
      return {
        title: t('components.dropzone.drop_file'),
        backgroundColor: 'rgb(49,164,255, 0.1)',
        textColor: 'info.main',
        iconColor: 'info.main',
      }
    return {
      title: t('components.dropzone.drag_and_drop'),
      backgroundColor: 'alabaster',
      titleColor: 'text.primary',
      textColor: 'primary.main',
      iconColor: 'primary.light',
    }
  }

  const { backgroundColor, textColor, iconColor, title, titleColor } =
    getDropZoneState()

  return (
    <Box
      {...getRootProps()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '214px',
        py: 3,
        bgcolor: backgroundColor,
        border: '1px dashed',
        borderColor: textColor,
        borderRadius: 1,
      }}
    >
      <input {...getInputProps()} />

      <IconUploadFile
        sx={{
          width: '80px',
          height: '80px',
          color: iconColor,
        }}
      />

      <Box>
        <Typography
          variant="caption1"
          color={titleColor ?? textColor}
          fontWeight={600}
        >
          {title}
        </Typography>

        {!isDragActive && !isError && (
          <Button
            variant="text"
            onClick={open}
            sx={{
              color: 'info.main',
              p: 0,
              fontSize: '16px',
              ml: 0.5,
              pt: 0.1,

              '&:hover': {
                background: 'none',
              },
            }}
          >
            {t('components.dropzone.browse')}
          </Button>
        )}
      </Box>

      {!isDragActive && children}
    </Box>
  )
}
